import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, numberAttribute, output, signal, viewChild } from '@angular/core';
import { MediaDto } from '@app/data/dto';
import { UploadService } from '@app/services/upload.service';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export type AppFiledropVariant = 'content' | 'inline' | 'modal';

export interface AppFiledropRejectedFile {
  readonly file: File;
  readonly reason: 'max-files' | 'max-size' | 'extension' | 'upload';
  readonly message: string;
}

export interface AppFiledropUploadFileEvent {
  readonly uploadId: string;
  readonly file: File;
}

export interface AppFiledropUploadProgressEvent extends AppFiledropUploadFileEvent {
  readonly progress: number;
}

export interface AppFiledropUploadCompletedEvent extends AppFiledropUploadFileEvent {
  readonly media: MediaDto;
}

export interface AppFiledropUploadFailedEvent extends AppFiledropUploadFileEvent {
  readonly message: string;
}

@Component({
  selector: 'app-filedrop',
  imports: [AppIcon, NgTemplateOutlet, TranslatePipe],
  templateUrl: './app-filedrop.html',
  styleUrl: './app-filedrop.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppFiledrop {
  private readonly uploadService = inject(UploadService);
  private readonly translate = inject(TranslateService);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly variant = input<AppFiledropVariant>('inline');
  readonly mediaUpload = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly multiple = input<boolean, unknown>(true, { transform: booleanAttribute });
  readonly maxFileSizeMb = input<number, unknown>(10, { transform: numberAttribute });
  readonly maxFiles = input<number, unknown>(10, { transform: numberAttribute });
  readonly extensions = input<readonly string[]>(['.pdf']);
  readonly showUploadQueue = input<boolean, unknown>(true, { transform: booleanAttribute });
  readonly maxConcurrentUploads = input<number, unknown>(3, { transform: numberAttribute });
  readonly uploadQueueDisplayThreshold = input<number, unknown>(3, { transform: numberAttribute });

  readonly overlayText = input(this.translate.instant('shared.filedrop.overlayText'));
  readonly title = input(this.translate.instant('shared.filedrop.title'));
  readonly subtitle = input(this.translate.instant('shared.filedrop.subtitle'));
  readonly buttonText = input(this.translate.instant('shared.filedrop.buttonText'));
  readonly supportedTypesTitle = input(this.translate.instant('shared.filedrop.supportedTypesTitle'));
  readonly maxFileSizeText = input(this.translate.instant('shared.filedrop.maxFileSizeText'));

  readonly filesSelected = output<readonly File[]>();
  readonly mediaUploaded = output<readonly MediaDto[]>();
  readonly mediaUploadStarted = output<AppFiledropUploadFileEvent>();
  readonly mediaUploadProgress = output<AppFiledropUploadProgressEvent>();
  readonly mediaUploadCompleted = output<AppFiledropUploadCompletedEvent>();
  readonly mediaUploadFailed = output<AppFiledropUploadFailedEvent>();
  readonly filesRejected = output<readonly AppFiledropRejectedFile[]>();

  protected readonly isDragging = signal(false);
  protected readonly isModalOpen = signal(false);
  protected readonly isUploading = signal(false);
  protected readonly selectedFileNames = signal<readonly string[]>([]);
  protected readonly rejectionMessages = signal<readonly string[]>([]);

  protected readonly normalizedExtensions = computed(() => this.extensions().map((extension) => this.normalizeExtension(extension)));
  protected readonly accept = computed(() => this.normalizedExtensions().join(','));
  protected readonly maxFileSizeLabel = computed(() => this.translate.instant('shared.filedrop.maxFileSizeLabel', { label: this.maxFileSizeText(), size: this.maxFileSizeMb() }));

  openFileDialog(): void {
    if (this.disabled() || this.isUploading()) {
      return;
    }

    this.fileInput()?.nativeElement.click();
  }

  protected openModal(): void {
    if (this.variant() !== 'modal' || this.disabled()) {
      return;
    }

    this.isModalOpen.set(true);
  }

  protected closeModal(): void {
    this.isModalOpen.set(false);
    this.isDragging.set(false);
  }

  protected onFileInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    void this.handleFiles(inputElement.files);
    inputElement.value = '';
  }

  protected onDragEnter(event: DragEvent): void {
    if (!this.canHandleDrop()) {
      return;
    }

    event.preventDefault();
    this.isDragging.set(true);
  }

  protected onDragOver(event: DragEvent): void {
    if (!this.canHandleDrop()) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  protected onDragLeave(event: DragEvent): void {
    if (!this.canHandleDrop()) {
      return;
    }

    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;
    if (currentTarget instanceof Node && relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) {
      return;
    }

    this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (!this.canHandleDrop()) {
      return;
    }

    event.preventDefault();
    this.isDragging.set(false);
    void this.handleFiles(event.dataTransfer?.files ?? null);
  }

  private canHandleDrop(): boolean {
    return !this.disabled() && !this.isUploading();
  }

  private async handleFiles(fileList: FileList | null): Promise<void> {
    const files = Array.from(fileList ?? []);
    const { acceptedFiles, rejectedFiles } = this.validateFiles(files);

    this.selectedFileNames.set(acceptedFiles.map((file) => file.name));
    this.rejectionMessages.set(rejectedFiles.map((file) => file.message));

    if (rejectedFiles.length) {
      this.filesRejected.emit(rejectedFiles);
    }

    if (!acceptedFiles.length) {
      return;
    }

    this.filesSelected.emit(acceptedFiles);

    if (!this.mediaUpload()) {
      this.closeModal();
      return;
    }

    await this.uploadMediaFiles(acceptedFiles);
  }

  private validateFiles(files: readonly File[]): { acceptedFiles: readonly File[]; rejectedFiles: readonly AppFiledropRejectedFile[] } {
    const acceptedFiles: File[] = [];
    const rejectedFiles: AppFiledropRejectedFile[] = [];
    const maxBytes = this.maxFileSizeMb() * 1024 * 1024;
    const allowedExtensions = this.normalizedExtensions();

    for (const file of files) {
      if (acceptedFiles.length >= this.maxFiles()) {
        rejectedFiles.push(this.createRejectedFile(file, 'max-files', this.translate.instant('shared.filedrop.errors.maxFiles', { count: this.maxFiles() })));
        continue;
      }

      if (file.size > maxBytes) {
        rejectedFiles.push(this.createRejectedFile(file, 'max-size', this.translate.instant('shared.filedrop.errors.maxSize', { name: file.name, size: this.maxFileSizeMb() })));
        continue;
      }

      if (!allowedExtensions.includes(this.getFileExtension(file.name))) {
        rejectedFiles.push(this.createRejectedFile(file, 'extension', this.translate.instant('shared.filedrop.errors.extension', { name: file.name })));
        continue;
      }

      acceptedFiles.push(file);
    }

    return { acceptedFiles, rejectedFiles };
  }

  private async uploadMediaFiles(files: readonly File[]): Promise<void> {
    this.isUploading.set(true);

    try {
      const queueGroupId = crypto.randomUUID();
      const uploadResults = await Promise.all(files.map(async (file) => {
        const uploadId = crypto.randomUUID();
        this.mediaUploadStarted.emit({ uploadId, file });
        try {
          const uploadedFile = await this.uploadMediaFile(uploadId, file, queueGroupId);
          this.mediaUploadCompleted.emit({ uploadId, file, media: uploadedFile });
          return { file, media: uploadedFile, message: null };
        }
        catch (error) {
          const message = error instanceof Error && error.message
            ? error.message
            : this.translate.instant('shared.filedrop.errors.upload', { name: file.name });
          this.mediaUploadFailed.emit({ uploadId, file, message });
          return { file, media: null, message };
        }
      }));

      const uploadedFiles = uploadResults.map((result) => result.media).filter((media): media is MediaDto => media !== null);
      const rejectedFiles = uploadResults
        .filter((result) => result.media === null)
        .map((result) => this.createRejectedFile(result.file, 'upload', result.message ?? this.translate.instant('shared.filedrop.errors.upload', { name: result.file.name })));

      if (rejectedFiles.length) {
        this.filesRejected.emit(rejectedFiles);
        this.rejectionMessages.set(rejectedFiles.map((file) => file.message));
      }

      if (uploadedFiles.length) {
        this.mediaUploaded.emit(uploadedFiles);
      }

      if (uploadedFiles.length === files.length) {
        this.closeModal();
      }
    }
    finally {
      this.isUploading.set(false);
    }
  }

  private async uploadMediaFile(uploadId: string, file: File, queueGroupId: string): Promise<MediaDto> {
    this.mediaUploadProgress.emit({ uploadId, file, progress: 0 });
    return this.uploadService.upload(file, {
      queueGroupId,
      maxConcurrentUploads: this.maxConcurrentUploads(),
      showInQueue: this.showUploadQueue(),
      queueDisplayThreshold: this.uploadQueueDisplayThreshold(),
      progressChanged: (item) => {
        this.mediaUploadProgress.emit({ uploadId, file, progress: this.clampProgress(item.progress) });
      },
    });
  }

  private createRejectedFile(file: File, reason: AppFiledropRejectedFile['reason'], message: string): AppFiledropRejectedFile {
    return { file, reason, message };
  }

  private getFileExtension(fileName: string): string {
    const extension = fileName.split('.').pop();
    return extension ? this.normalizeExtension(extension) : '';
  }

  private normalizeExtension(extension: string): string {
    const normalizedExtension = extension.trim().toLocaleLowerCase();
    return normalizedExtension.startsWith('.') ? normalizedExtension : `.${normalizedExtension}`;
  }

  private clampProgress(progress: number): number {
    if (!Number.isFinite(progress)) {
      return 0;
    }

    return Math.min(100, Math.max(0, Math.round(progress)));
  }
}
